/**
 * StripeInsightsWidget — Compacte "Vandaag via Stripe" tegel op het hoofd-dashboard.
 *
 * Toont realtime KPIs (omzet vandaag, mislukte events, wachtende webhooks) via
 * ``GET /api/admin/stripe/dashboard`` en ``GET /api/admin/stripe/status``.
 * Klik opent Instellingen → Productieconfiguratie → Stripe status.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, useAuth } from "../../context/AuthContext";
import { CreditCard, TrendingUp, Send, AlertTriangle, ChevronRight, Zap } from "lucide-react";
import { AdminSectionCard } from "./ui";

function fmtMoney(v, cur = "EUR") {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: (cur || "EUR").toUpperCase(),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${cur} ${Number(v).toFixed(2)}`; }
}

export default function StripeInsightsWidget() {
  const { hasPermission } = useAuth();
  // Sprint 2 (Feb 2026) — Guard: Stripe endpoints (/admin/stripe/dashboard
  // + /admin/stripe/status) vereisen `stripe:view`. Voor rollen die die
  // permissie niet hebben (medewerker, content_manager, webshop_manager)
  // renderden we voorheen de widget en fireden we onnodig 2 x 403 per
  // dashboard-render. We tonen nu niets voor die gebruikers — de widget
  // hoort thuis in Financieel en is niet relevant voor operationele rollen.
  const canView = hasPermission("stripe:view");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(canView);

  const load = useCallback(async () => {
    if (!canView) return;  // extra safeguard bij re-triggered load()
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        adminApi.get("/admin/stripe/dashboard"),
        adminApi.get("/admin/stripe/status"),
      ]);
      setData(d.data);
      setStatus(s.data);
    } catch {
      // stil falen — widget hoeft geen error-toasts te tonen op dashboard
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  // Geen permissie → widget verbergt zich volledig. Dashboard layout is
  // grid-based en tolereert een missende tegel prima; er is geen "loading"
  // spinner nodig omdat de widget nooit zichtbaar wordt voor deze rol.
  if (!canView) return null;

  const mode = status?.keys?.secret_mode || "missing";
  const modeBadge = mode === "live"
    ? { text: "LIVE", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" }
    : mode === "test"
      ? { text: "TEST", cls: "bg-blue-100 text-blue-800 border-blue-200" }
      : { text: "NIET INGESTELD", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  const blockers = status?.production_guard?.blockers?.length || 0;

  return (
    <AdminSectionCard
      title="Stripe vandaag"
      subtitle={loading ? "Laden…" : "Klik voor volledig dashboard →"}
      actions={
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-chivo font-bold uppercase tracking-wide ${modeBadge.cls}`}>
          {modeBadge.text}
        </span>
      }
      testid="stripe-insights-widget"
    >
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-8 w-1/3 bg-slate-200 rounded" />
          <div className="h-3 w-1/2 bg-slate-100 rounded" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniKpi
              icon={TrendingUp}
              label="Omzet vandaag"
              value={fmtMoney(data?.revenue_today)}
              tone="emerald"
              testid="widget-revenue-today"
            />
            <MiniKpi
              icon={CreditCard}
              label="Betalingen"
              value={data?.payments_today_count ?? 0}
              tone="brand"
              testid="widget-payments-today"
            />
            <MiniKpi
              icon={Send}
              label="Open sessies"
              value={data?.open_checkout_sessions ?? 0}
              tone="amber"
              testid="widget-open-sessions"
            />
            <MiniKpi
              icon={AlertTriangle}
              label="Fouten (24u)"
              value={data?.failed_events_last_24h ?? 0}
              tone={(data?.failed_events_last_24h || 0) > 0 ? "red" : "slate"}
              testid="widget-failed-24h"
            />
          </div>

          {blockers > 0 && (
            <div className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 flex items-start gap-2 text-xs text-red-900" data-testid="widget-blocker-notice">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-chivo font-bold">Production Guard actief:</span> {blockers} probleem
                {blockers > 1 ? "en" : ""} — open Instellingen → Productieconfiguratie → Stripe.
              </div>
            </div>
          )}

          <div className="mt-3 text-right">
            <a
              href="#stripe-status"
              className="inline-flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700 font-chivo font-bold"
              data-testid="widget-details-link"
            >
              <Zap className="w-3 h-3" /> Bekijk volledig Stripe dashboard <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </>
      )}
    </AdminSectionCard>
  );
}

function MiniKpi({ icon: Icon, label, value, tone, testid }) {
  const toneCls = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    brand: "bg-brand-50 border-brand-100 text-brand-900",
    red: "bg-red-50 border-red-200 text-red-900",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.14em] font-chivo font-bold opacity-70">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="mt-1 text-lg font-chivo font-bold font-mono-num tabular-nums">{value ?? "—"}</div>
    </div>
  );
}
